'use client';

import React, { useEffect } from 'react';
import { X, Clock, Tag } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

export interface GuideContent {
  id: string;
  title: string;
  timeEstimate: string;
  difficulty: string;
  tags: string[];
  sections: {
    heading?: string;
    body: string;
    type: 'intro' | 'section' | 'list' | 'note' | 'steps';
    items?: string[];
  }[];
}

interface GuideModalProps {
  guide: GuideContent | null;
  onClose: () => void;
}

const difficultyConfig: Record<string, { label: string; color: string }> = {
  Débutant: { label: 'Débutant', color: 'text-green-700 bg-green-50 border-green-200' },
  Intermédiaire: { label: 'Intermédiaire', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  Avancé: { label: 'Avancé', color: 'text-red-700 bg-red-50 border-red-200' },
};

export default function GuideModal({ guide, onClose }: GuideModalProps) {
  const { t, isRTL } = useLanguage();
  useEffect(() => {
    if (!guide) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [guide, onClose]);

  if (!guide) return null;

  const diff = difficultyConfig[guide.difficulty];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guide-modal-title"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <h2
              id="guide-modal-title"
              className="font-jakarta text-xl font-700 text-gray-900 leading-snug"
            >
              {guide.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {diff && (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${diff.color}`}>
                  {diff.label}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Clock size={12} />
                {guide.timeEstimate}
              </span>
              {guide.tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-500"
                >
                  <Tag size={10} />
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t.modal.close}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {guide.sections.map((section, idx) => {
            if (section.type === 'intro') {
              return (
                <p key={idx} className="text-gray-600 text-[15px] leading-relaxed">
                  {section.body}
                </p>
              );
            }

            if (section.type === 'note') {
              return (
                <div key={idx} className="bg-primary-50 border border-primary-100 rounded-xl px-4 py-3">
                  {section.heading && (
                    <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-1">
                      {section.heading}
                    </p>
                  )}
                  <p className="text-sm text-primary-800 leading-relaxed">{section.body}</p>
                </div>
              );
            }

            if (section.type === 'steps' && section.items) {
              return (
                <div key={idx}>
                  {section.heading && (
                    <h3 className="font-jakarta text-base font-600 text-gray-800 mb-3">
                      {section.heading}
                    </h3>
                  )}
                  <ol className="space-y-2.5">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex gap-3 text-sm text-gray-600 leading-relaxed">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-700 text-white text-xs font-semibold flex items-center justify-center mt-0.5">
                          {i + 1}
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              );
            }

            if (section.type === 'list' && section.items) {
              return (
                <div key={idx}>
                  {section.heading && (
                    <h3 className="font-jakarta text-base font-600 text-gray-800 mb-3">
                      {section.heading}
                    </h3>
                  )}
                  <ul className="space-y-2">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-gray-600 leading-relaxed">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500 mt-2" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }

            // default: 'section'
            return (
              <div key={idx}>
                {section.heading && (
                  <h3 className="font-jakarta text-base font-600 text-gray-800 mb-2">
                    {section.heading}
                  </h3>
                )}
                <p className="text-sm text-gray-600 leading-relaxed">{section.body}</p>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            {t.modal.officialSources}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-primary-700 text-white hover:bg-primary-800 transition-colors"
          >
            {t.modal.close}
          </button>
        </div>
      </div>
    </div>
  );
}
